import { Copyright } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  CssBaseline,
  Grid,
  Paper,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";
import axios from "axios";
import { useState } from "react";
import { useSignIn } from "react-auth-kit";
import bcrypt from "bcryptjs";
import { useNavigate } from "react-router-dom";
import localize from "../../common/localize";
import ApplicationLogoVertical from "../../components/common/ApplicationLogoVertical";
import { parseJwt } from "../../common/utils";
import { AUTH_ENDPOINT } from "../../services/config";

export default function Login() {
  const [password, setPassword] = useState("");
  const [userName, setUserName] = useState("");
  const [errorMessage, setErrorMessage] = useState(
    `${localize.check_credentials}`,
  );
  const signIn = useSignIn();
  const navigate = useNavigate();
  const [errorSnack, setErrorSnack] = useState();

  const assignUserName = (event) => {
    setUserName(event.target.value);
  };

  const assignPassword = (event) => {
    setPassword(event.target.value);
  };

  const throwError = (message) => {
    setErrorSnack(true);
    setErrorMessage(message);
  };

  const handleLogin = async () => {
    const hashedPassword = bcrypt.hashSync(
      password,
      process.env.REACT_APP_BCRYPT_SALT,
    );

    try {
      const result = await axios.post(
        `${AUTH_ENDPOINT}/authenticate`,
        {
          UserName: userName,
          Password: hashedPassword,
        },
        {
          headers: {
            ApplicationId: localize.app_name,
            pagination: 0,
          },
        },
      );
      if (result.data.ResultCode === 0) {
        const parsedData = parseJwt(result.data.Data);
        const userData = JSON.parse(parsedData.data);

        try {
          signIn({
            token: result.data.Data,
            tokenType: "bearer",
            authState: userData,
            expiresIn: 1440,
          });
          navigate("/");
        } catch (error) {
          throwError(`${localize.error_unexpected}: USER DATA BAD FORMED`);
        }
      } else if (result.data.ResultCode === 101) {
        throwError(localize.invalid_credentials);
      } else {
        throwError(`Server Error: ${result.data.Message}`);
      }
    } catch (error) {
      throwError(`${localize.error_unexpected}: ${error.message}`);
    }
  };

  const keyPressValidation = (event) => {
    if (event.key === "Enter") {
      handleLogin();
    }
  };

  return (
    <div className="flex-col items-center">
      <Grid
        container
        component="main"
        elevation={10}
        style={{ height: "100vh" }}
      >
        <Snackbar
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          open={errorSnack}
          autoHideDuration={2500}
          onClose={() => {
            setErrorSnack(false);
          }}
        >
          <Alert severity="error" sx={{ width: "100%" }}>
            <label className="text-xs">{errorMessage}</label>
          </Alert>
        </Snackbar>
        <CssBaseline />

        <div
          className="flex items-center justify-center w-full"
          style={{ backgroundColor: "#DDDEDD" }}
        >
          <div
            className="flex"
            style={{
              width: "100%",
              maxWidth: "900px",
            }}
          >
            <Grid
              item
              xs={false}
              sm={4}
              md={5}
              sx={{
                display: { xs: "none", sm: "block" }, // 👈 OCULTAR EN MÓVIL
                borderRadius: "16px 0px 0px 16px",
                padding: "50px",
                backgroundImage: `linear-gradient(
      rgba(255, 255, 255, 0.1),
      rgba(255, 255, 255, 0.1)
    ),
    url(${process.env.PUBLIC_URL}/img/login_photo_3.PNG)`,
                backgroundRepeat: "no-repeat",
                backgroundColor: (t) =>
                  t.palette.mode === "light"
                    ? t.palette.grey[50]
                    : t.palette.grey[900],
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
            >
              <Box className="flex flex-col w-full h-full justify-center gap-4">
                <Typography sx={{ color: "white", fontSize: "20px" }}>
                  ✔ Gestione servicios
                </Typography>
                <Typography sx={{ color: "white", fontSize: "20px" }}>
                  ✔ Valide su ejecución
                </Typography>
                <Typography sx={{ color: "white", fontSize: "20px" }}>
                  ✔ Documente la operación
                </Typography>
                <Typography sx={{ color: "white", fontSize: "20px" }}>
                  ✔ Administre los recursos
                </Typography>
                <Typography
                  variant="h4"
                  sx={{ color: "white", marginTop: "30px" }}
                >
                  No controle, <b>Sólo Koordine</b>
                </Typography>
              </Box>
            </Grid>
            <Grid
              item
              xs={12}
              sm={8}
              md={7}
              component={Paper}
              sx={{
                borderRadius: {
                  xs: "16px",
                  sm: "0px 16px 16px 0px",
                },
              }}
            >
              <Box
                sx={{
                  my: 8,
                  mx: 4,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  height: "100%",
                }}
              >
                <ApplicationLogoVertical height={150} width={150} />
                <div className="mb-5" />
                <Box component="form" sx={{ mt: 1 }}>
                  <TextField
                    margin="normal"
                    required
                    fullWidth
                    id="username"
                    onChange={assignUserName}
                    label={localize.username}
                    autoFocus
                    onKeyUp={keyPressValidation}
                  />
                  <TextField
                    margin="normal"
                    required
                    fullWidth
                    name="password"
                    label={localize.password}
                    onChange={assignPassword}
                    type="password"
                    id="password"
                    onKeyUp={keyPressValidation}
                  />
                  <Button
                    sx={{
                      fontSize: "0.8em",
                      mt: 3,
                      mb: 2,
                      backgroundColor: "#10366E",
                    }}
                    size="large"
                    variant="contained"
                    fullWidth
                    disabled={userName.length <= 0 || password.length <= 0}
                    onClick={handleLogin}
                    className="btn-success app_general_button_data"
                  >
                    {localize.enter}
                  </Button>
                  <div className="flex justify-center" style={{ gap: "10px" }}>
                    <Copyright />
                    {`${localize.app_name} ${
                      localize.version
                    } - ${new Date().getFullYear()}`}{" "}
                  </div>
                </Box>
              </Box>
            </Grid>
          </div>
        </div>
      </Grid>
    </div>
  );
}
